import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Check, X, Package, ArrowRight, ShieldCheck } from 'lucide-react';

export interface AiMateriale {
  descrizione: string;
  quantita: string | number;
  um?: 'N.' | 'MT.' | string;
}

export interface AiRiformulaModalProps {
  isOpen: boolean;
  onClose: () => void;
  testoOriginale: string;
  testoRiformulato: string;
  materiali: AiMateriale[];
  modifiche: string[];
  onApplica: (testo: string, materialiDaAggiungere: AiMateriale[]) => void;
}

export function AiRiformulaModal({
  isOpen,
  onClose,
  testoOriginale,
  testoRiformulato,
  materiali,
  modifiche,
  onApplica,
}: AiRiformulaModalProps) {
  const [includiMateriali, setIncludiMateriali] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const handleConferma = () => {
    onApplica(testoRiformulato, includiMateriali ? materiali : []);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full max-w-xl rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh] overflow-hidden animate-slide-up border border-slate-200">
        {/* Header Modale */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-gradient-to-r from-emerald-50 via-teal-50 to-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold shadow-xs">
              <Sparkles className="w-5 h-5 text-emerald-100" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="text-base sm:text-lg font-black text-slate-900 leading-tight">
                  Revisione Tecnica AI
                </h3>
                <span className="badge-verde text-[10px] font-extrabold uppercase px-2 py-0.5">
                  Glossario Capitolato
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium">
                Correzione refusi vocali e terminologia semaforica ufficiale
              </p>
            </div>
          </div>
          <button
            type="button"
            className="w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-700 flex items-center justify-center transition"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Corpo Modale Scrollabile */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4 text-xs sm:text-sm">
          {/* Confronto Testi */}
          <div className="space-y-3">
            {/* Testo Originale */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
              <div className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                Bozza / Dettatura originale
              </div>
              <div className="text-slate-600 italic font-mono text-xs whitespace-pre-wrap leading-relaxed line-clamp-4">
                "{testoOriginale}"
              </div>
            </div>

            {/* Freccia */}
            <div className="flex items-center justify-center gap-1.5 text-emerald-600 font-bold text-xs">
              <ArrowRight className="w-4 h-4" />
              <span>Testo Riformulato per il Verbale</span>
            </div>

            {/* Testo Riformulato */}
            <div className="rounded-2xl border-2 border-emerald-500/80 bg-emerald-50/40 p-3.5 shadow-2xs">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-black text-emerald-900 uppercase tracking-wider flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Descrizione tecnica validata
                </span>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-lg">
                  Pronto per il Committente
                </span>
              </div>
              <div className="text-slate-900 font-medium text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">
                {testoRiformulato}
              </div>
            </div>
          </div>

          {/* Modifiche rilevate */}
          {modifiche && modifiche.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5">
              <div className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                Correzioni e termini applicati:
              </div>
              <ul className="space-y-1">
                {modifiche.map((mod, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                    <span className="text-emerald-600 font-bold">•</span>
                    <span>{mod}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Materiali rilevati automaticamente */}
          {materiali && materiali.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3.5 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-amber-900 font-extrabold text-xs">
                  <Package className="w-4 h-4 text-amber-600" />
                  <span>Ricambi rilevati dal testo ({materiali.length})</span>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs font-bold text-amber-900 select-none">
                  <input
                    type="checkbox"
                    className="checkbox text-amber-600 rounded"
                    checked={includiMateriali}
                    onChange={(e) => setIncludiMateriali(e.target.checked)}
                  />
                  <span>Importa nel Passo 3</span>
                </label>
              </div>

              <div className="space-y-1.5">
                {materiali.map((mat, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded-xl bg-white border border-amber-200 text-xs font-semibold text-slate-800"
                  >
                    <span className="truncate pr-2">{mat.descrizione}</span>
                    <span className="badge-amber shrink-0 font-bold">
                      {mat.um || 'N.'} {mat.quantita}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer con Azioni */}
        <div className="p-4 sm:p-5 border-t border-slate-100 flex items-center justify-end gap-2.5 bg-slate-50 shrink-0">
          <button
            type="button"
            className="btn-outline px-4 py-2.5 text-xs font-bold"
            onClick={onClose}
          >
            Mantieni originale
          </button>
          <button
            type="button"
            className="btn-primary px-5 py-2.5 text-xs font-bold gap-2 shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleConferma}
          >
            <Check className="w-4 h-4" />
            <span>Applica al Report</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
